import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { fmt } from "../../lib/money";
import { Search, Trash2, Tag, ChevronDown, ChevronUp, MapPin, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";

const STATUS_COLORS = {
  placed: "bg-[#FFE600] text-black",
  shipped: "bg-[#0033FF] text-white",
  delivered: "bg-[#00A355] text-white",
  cancelled: "bg-[#FF2A00] text-white",
};

/**
 * Global admin view of every order on the platform. Search, filter by status,
 * change status, delete (with optional stock restore), drill into details.
 */
export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (status !== "all") params.set("status", status);
      if (q.trim()) params.set("q", q.trim());
      const { data } = await api.get(`/admin/orders?${params.toString()}`);
      setOrders(data || []);
    } catch {
      toast.error("Couldn't load orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [status]);

  // Clear the header bell badge — admin is looking at orders now.
  useEffect(() => {
    api.post("/notifications/mark-seen").catch(() => {});
    try { window.dispatchEvent(new Event("orders-marked-seen")); } catch { /* ignore */ }
  }, []);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(load, 350);
    return () => clearTimeout(t);
  }, [q]);

  const changeStatus = async (orderId, next) => {
    try {
      await api.put(`/admin/orders/${orderId}`, { status: next });
      toast.success(`Status → ${next}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const del = async (orderId) => {
    const restore = window.confirm(
      `Delete order ${orderId.slice(-8)}?\n\nOK = also RESTORE the ordered units back to product stock (recommended for cancellations).\nCancel = delete WITHOUT restoring stock.`
    );
    const reallyDelete = window.confirm("Permanently delete this order? A tombstone is kept in `deleted_orders` for audit. Proceed?");
    if (!reallyDelete) return;
    try {
      await api.delete(`/admin/orders/${orderId}?restore_stock=${restore}`);
      toast.success("Order deleted" + (restore ? " · stock restored" : ""));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const totals = useMemo(() => ({
    count: orders.length,
    revenue: orders.reduce((s, o) => s + (o.total || 0), 0),
  }), [orders]);

  return (
    <div>
      <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight mb-2">All orders</h1>
      <p className="text-sm text-neutral-600 mb-6">Every order across the platform. Search, change status, or delete.</p>

      <div className="grid sm:grid-cols-[1fr_auto] gap-2 mb-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input
            data-testid="admin-orders-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Order id, customer email/name, product, affiliate slug…"
            className="w-full h-10 pl-9 pr-3 border-2 border-black rounded-none text-sm"
          />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="rounded-none border-2 border-black h-10 w-44 text-sm" data-testid="admin-orders-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-none border-black">
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="placed">Placed</SelectItem>
            <SelectItem value="shipped">Shipped</SelectItem>
            <SelectItem value="delivered">Delivered</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="text-xs text-neutral-500 mb-3">
        {loading ? "Loading…" : `${totals.count} order${totals.count === 1 ? "" : "s"} · ${fmt(totals.revenue)} total`}
      </div>

      {!loading && orders.length === 0 && (
        <div className="border border-dashed border-neutral-300 p-8 text-center text-sm text-neutral-500">
          No orders match.
        </div>
      )}

      <div className="space-y-2">
        {orders.map((o) => {
          const isOpen = !!expanded[o.order_id];
          const a = o.shipping_address || {};
          return (
            <div key={o.order_id} className="border border-neutral-200" data-testid={`admin-order-${o.order_id}`}>
              <div className="p-3 sm:p-4 flex items-start justify-between gap-3 flex-wrap min-w-0">
                <div className="min-w-0 flex-1 basis-full sm:basis-auto">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      onClick={() => setExpanded((m) => ({ ...m, [o.order_id]: !isOpen }))}
                      className="inline-flex items-center gap-1.5 font-display font-bold text-base hover:underline"
                    >
                      {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      #{o.order_id.slice(-8)}
                    </button>
                    <span className={`text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 ${STATUS_COLORS[o.status]}`}>{o.status}</span>
                    {o.discount_codes?.length > 0 && (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 bg-[#FFE600] text-black border border-black">
                        <Tag className="w-3 h-3" /> {o.discount_codes[0]}{o.discount_codes.length > 1 ? ` +${o.discount_codes.length - 1}` : ""}
                      </span>
                    )}
                    {o.tm_ref && (
                      <code className="text-[10px] font-mono bg-[#0033FF]/10 text-[#0033FF] px-1.5 py-0.5">{o.tm_ref}</code>
                    )}
                  </div>
                  <div className="mt-1 text-[11px] sm:text-xs text-neutral-600">
                    {new Date(o.created_at).toLocaleString()} ·{" "}
                    <Link to="/admin/users" className="hover:underline">
                      <b className="text-black">{o.customer_name || a.full_name || "?"}</b>{o.customer_email ? ` · ${o.customer_email}` : ""}
                    </Link>
                    {" · "}{o.items.length} item{o.items.length === 1 ? "" : "s"}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0 flex-wrap basis-full sm:basis-auto justify-end">
                  <span className="font-display font-black">{fmt(o.total)}</span>
                  <Select value={o.status} onValueChange={(v) => changeStatus(o.order_id, v)}>
                    <SelectTrigger className="rounded-none border-black h-9 w-28 text-xs" data-testid={`admin-order-status-${o.order_id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none border-black">
                      <SelectItem value="placed">placed</SelectItem>
                      <SelectItem value="shipped">shipped</SelectItem>
                      <SelectItem value="delivered">delivered</SelectItem>
                      <SelectItem value="cancelled">cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                  <button
                    type="button"
                    onClick={() => del(o.order_id)}
                    data-testid={`admin-order-delete-${o.order_id}`}
                    title="Delete order"
                    className="h-9 w-9 inline-flex items-center justify-center border border-black hover:bg-[#FF2A00] hover:text-white hover:border-[#FF2A00]"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <OrderEditButton order={o} onSaved={load} />
                </div>
              </div>
              {isOpen && (
                <div className="px-3 sm:px-4 pb-3 sm:pb-4 border-t border-neutral-200 bg-neutral-50">
                  <div className="mt-2 text-xs text-neutral-700 flex items-start gap-1.5">
                    <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>
                      <b className="text-black">{a.full_name}</b>{a.phone ? ` · ${a.phone}` : ""}
                      <br />
                      {a.line1}{a.line2 ? `, ${a.line2}` : ""}, {a.city}, {a.state} {a.postal_code}, {a.country}
                    </span>
                  </div>
                  <div className="mt-3 pt-3 border-t border-neutral-200 space-y-1">
                    {o.items.map((it, idx) => (
                      <div key={`${it.product_id}-${idx}`} className="flex justify-between text-xs gap-3">
                        <span className="truncate">
                          {it.title} × {it.quantity}
                          {it.size && ` · ${it.size}`}{it.color && ` · ${it.color}`}
                          {it.seller_name && <span className="text-neutral-500"> · {it.seller_name}</span>}
                        </span>
                        <span className="font-semibold whitespace-nowrap">{fmt(it.price * it.quantity)}</span>
                      </div>
                    ))}
                  </div>
                  {o.discount_breakdown?.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-neutral-200 space-y-0.5">
                      {o.discount_breakdown.map((d, i) => (
                        <div key={i} className="flex justify-between text-xs text-[#00A355]">
                          <span>{d.code}</span>
                          <span>−{fmt(d.amount)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {orders.length >= 500 && (
        <div className="mt-4 text-[11px] text-[#B25E00] bg-[#FFF7E6] border border-[#FFCC66] px-3 py-2">
          Showing the 500 most recent matches. Refine your search to see older orders.
        </div>
      )}
    </div>
  );

// -------------------- inline order edit --------------------

function OrderEditButton({ order, onSaved }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`admin-order-edit-${order.order_id}`}
        title="Edit order"
        className="h-9 w-9 inline-flex items-center justify-center border border-black hover:bg-black hover:text-white"
      >
        <Pencil className="w-4 h-4" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none border-black max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-2xl">
              Edit order #{order.order_id.slice(-8)}
            </DialogTitle>
            <DialogDescription className="text-xs text-neutral-500">
              Admin override. Stock and webhooks are NOT re-fired.
            </DialogDescription>
          </DialogHeader>
          <OrderEditForm
            order={order}
            onSaved={() => { setOpen(false); onSaved?.(); }}
            onCancel={() => setOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

function OrderEditForm({ order, onSaved, onCancel }) {
  const a = order.shipping_address || {};
  const [form, setForm] = useState({
    status: order.status,
    tm_ref: order.tm_ref || "",
    subtotal: order.subtotal ?? "",
    shipping: order.shipping ?? "",
    discount_amount: order.discount_amount ?? "",
    total: order.total ?? "",
    notes: order.notes || "",
    full_name: a.full_name || "",
    phone: a.phone || "",
    line1: a.line1 || "",
    line2: a.line2 || "",
    city: a.city || "",
    state: a.state || "",
    postal_code: a.postal_code || "",
    country: a.country || "",
  });
  const [busy, setBusy] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        status: form.status,
        tm_ref: form.tm_ref || null,
        notes: form.notes || null,
        shipping_address: {
          full_name: form.full_name,
          phone: form.phone,
          line1: form.line1,
          line2: form.line2,
          city: form.city,
          state: form.state,
          postal_code: form.postal_code,
          country: form.country,
        },
      };
      for (const k of ["subtotal", "shipping", "discount_amount", "total"]) {
        if (form[k] !== "" && form[k] !== null && form[k] !== undefined) {
          const n = parseFloat(form[k]);
          if (Number.isFinite(n)) payload[k] = n;
        }
      }
      await api.put(`/admin/orders/${order.order_id}`, payload);
      toast.success("Order updated");
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const F = ({ label, k, type = "text", full }) => (
    <div className={`space-y-1 ${full ? "col-span-2" : ""}`}>
      <label className="text-[10px] uppercase tracking-widest font-bold text-neutral-600">{label}</label>
      <Input
        data-testid={`oef-${k}`}
        type={type}
        step={type === "number" ? "any" : undefined}
        value={form[k] ?? ""}
        onChange={(e) => update(k, e.target.value)}
        className="rounded-none border-black h-10"
      />
    </div>
  );

  return (
    <div className="space-y-4" data-testid="order-edit-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="text-[10px] uppercase tracking-widest font-bold text-neutral-600">Status</label>
          <Select value={form.status} onValueChange={(v) => update("status", v)}>
            <SelectTrigger data-testid="oef-status" className="rounded-none border-black h-10"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none border-black">
              <SelectItem value="placed">placed</SelectItem>
              <SelectItem value="shipped">shipped</SelectItem>
              <SelectItem value="delivered">delivered</SelectItem>
              <SelectItem value="cancelled">cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <F label="Affiliate ref (tm_ref)" k="tm_ref" />
        <F label="Subtotal" k="subtotal" type="number" />
        <F label="Shipping" k="shipping" type="number" />
        <F label="Discount amount" k="discount_amount" type="number" />
        <F label="Total" k="total" type="number" />
      </div>

      <div className="border-t border-neutral-200 pt-3">
        <div className="text-[11px] uppercase tracking-widest font-bold text-neutral-700 mb-2">Shipping address</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <F label="Full name" k="full_name" full />
          <F label="Phone" k="phone" />
          <F label="Line 1" k="line1" full />
          <F label="Line 2" k="line2" full />
          <F label="City" k="city" />
          <F label="State / Region" k="state" />
          <F label="Postal code" k="postal_code" />
          <F label="Country" k="country" />
        </div>
      </div>

      <div className="space-y-1">
        <label className="text-[10px] uppercase tracking-widest font-bold text-neutral-600">Notes (admin)</label>
        <textarea
          data-testid="oef-notes"
          value={form.notes}
          onChange={(e) => update("notes", e.target.value)}
          rows={2}
          className="w-full rounded-none border border-black p-2 text-sm"
        />
      </div>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" className="rounded-none border-black h-10 px-4" onClick={onCancel}>Cancel</Button>
        <Button data-testid="order-edit-save" type="button" disabled={busy} onClick={save} className="rounded-none bg-black hover:bg-neutral-800 h-10 px-5">
          {busy ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </div>
  );
}

}
